import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import React, { useEffect, useState } from 'react';

import { useBookingPaymentProviderDetails } from '@/booking-payment/hooks/use-booking-payment-provider-details.hook';
import { NoSSR } from '@/common/components/utils';
import { useLocale } from '@/i18n';
import { useLogger } from '@/logging';
import { useTheme } from '@/ui/theme';
import { createCSSFontFaceSourceString } from '@/ui/typography';

import { useStripeElementsAppearance } from '../hooks/useStripeElementsAppearance.hook';

interface StripeProviderWithElementsProps {
  children: React.ReactNode;
  bookingId: string;
  hotelSlug: string;
}

export const StripeProviderWithElements = (
  props: StripeProviderWithElementsProps
) => {
  const { fontFaceDefinitions } = useTheme();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const logger = useLogger();
  const locale = useLocale();

  const { data: provider } = useBookingPaymentProviderDetails({
    bookingId: props.bookingId,
    hotelReferenceId: props.hotelSlug,
  });

  const appearance = useStripeElementsAppearance();

  const publicKey = provider.paymentIntent.publicKey;

  useEffect(() => {
    if (!publicKey) return;

    setStripePromise(() =>
      loadStripe(publicKey, {
        stripeAccount: provider.connectedAccountId,
      })
    );
  }, [publicKey, provider.connectedAccountId]);

  if (!publicKey) {
    logger.error(
      'Unable to render component, missing Stripe API key for this hotel configuration.'
    );
    return null;
  }

  const fonts = fontFaceDefinitions.map((font) => ({
    src: createCSSFontFaceSourceString(font, document.location.origin),
    family: font.fontFamily,
    weight: font.fontWeight.toString(),
    style: font.fontStyle ?? 'normal',
    display: 'swap',
  }));

  return (
    <NoSSR>
      {/* Add fonts directly to Stripe Elements' iframe */}
      <Elements
        options={{
          locale: locale.baseName as StripeElementLocale,
          clientSecret: provider.paymentIntent.clientSecret,
          fonts,
          // https://stripe.com/docs/elements/appearance-api#rules
          appearance,
        }}
        stripe={stripePromise ?? null}
      >
        {props.children}
      </Elements>
    </NoSSR>
  );
};
